import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import { Button, Modal, Text, TextInput, useTheme } from '@/components/ui';

/**
 * Add a request type the ruleset does not have yet (#369).
 *
 * Homeowners ask for things nobody wrote a rule for. A driveway replacement arrives at an
 * association whose ruleset has 22 types and none of them is a driveway, and the only
 * honest options are to mis-file it under something close or dump it in Uncategorized —
 * where it carries no requirements at all and every future one lands the same way.
 *
 * The CHECKLIST is the reason this dialog exists rather than a bare name field. A type
 * with no requirements is just a label; the point of creating "Driveway / Blacktop" is
 * that the next one is measured against the right list without anyone remembering to.
 */

/** Stable key from a label: what the checklist and attachment assignments are stored under. */
const toKey = (label: string): string =>
	label.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '_').slice(0, 40);

const toSlug = (name: string): string =>
	name.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').slice(0, 60);

interface DraftItem {
	label: string;
	required: boolean;
}

export interface RequestTypeCreateModalProps {
	visible: boolean;
	/** Pre-fills the name with whatever was typed into the picker that found nothing. */
	initialName?: string;
	busy?: boolean;
	onDismiss: () => void;
	onCreate: (values: {
		name: string;
		slug: string;
		requiredAttachments: Array<{ key: string; label: string; required: boolean }>;
		reviewDeadlineDays: number;
	}) => void;
}

export const RequestTypeCreateModal: React.FC<RequestTypeCreateModalProps> = ({
	visible, initialName = '', busy = false, onDismiss, onCreate,
}) => {
	const theme = useTheme();
	const [name, setName] = useState('');
	const [days, setDays] = useState('30');
	const [items, setItems] = useState<DraftItem[]>([]);
	const [draft, setDraft] = useState('');

	useEffect(() => {
		if (!visible) return;
		setName(initialName);
		setDays('30');
		// Every type starts with the one requirement all of them share; the rest is
		// whatever this improvement actually needs.
		setItems([{ label: 'Completed and detailed application form', required: true }]);
		setDraft('');
	}, [visible, initialName]);

	const addItem = () => {
		const label = draft.trim();
		if (!label) return;
		setItems((prev) => [...prev, { label, required: true }]);
		setDraft('');
	};

	return (
		<Modal visible={visible} onDismiss={onDismiss}>
			<View style={{ padding: 20, width: 540, maxWidth: '100%' }}>
				<Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 2 }}>New request type</Text>
				<Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 14 }}>
					Added to the shared ruleset, so every association can use it.
				</Text>

				<ScrollView style={{ maxHeight: 420 }}>
					<Text style={{ fontWeight: '700', marginBottom: 4 }}>Name</Text>
					<TextInput
						value={name}
						onChangeText={setName}
						placeholder="e.g. Driveway / Blacktop"
						testID="request-type-name-input"
					/>

					<Text style={{ fontWeight: '700', marginTop: 14, marginBottom: 4 }}>Review deadline (days)</Text>
					<TextInput
						value={days}
						onChangeText={setDays}
						keyboardType="number-pad"
						testID="request-type-days-input"
					/>

					<Text style={{ fontWeight: '700', marginTop: 16, marginBottom: 2 }}>Submission requirements</Text>
					<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant, marginBottom: 8 }}>
						What a homeowner must send for this to be reviewable. Tap an item to toggle
						required / optional.
					</Text>

					{items.map((item, index) => (
						<View
							key={`${item.label}-${index}`}
							style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 4 }}
						>
							<Pressable
								onPress={() => setItems((prev) => prev.map((it, i) => (
									i === index ? { ...it, required: !it.required } : it
								)))}
								testID={`requirement-toggle-${index}`}
								style={{ flexDirection: 'row', alignItems: 'center', flex: 1, minWidth: 0, paddingVertical: 4 }}
							>
								<Ionicons
									name={item.required ? 'checkbox' : 'square-outline'}
									size={18}
									color={item.required ? theme.colors.primary : theme.colors.onSurfaceVariant}
									style={{ marginRight: 8 }}
								/>
								<Text style={{ flex: 1, minWidth: 0, color: theme.colors.onSurface }}>
									{item.label}{item.required ? '' : ' (optional)'}
								</Text>
							</Pressable>
							<Pressable
								onPress={() => setItems((prev) => prev.filter((_, i) => i !== index))}
								testID={`requirement-remove-${index}`}
								// Real padding: RN-Web drops hitSlop, and a near-miss would toggle
								// the row instead of removing it.
								style={{ paddingVertical: 6, paddingHorizontal: 8 }}
							>
								<Ionicons name="close" size={16} color={theme.colors.error} />
							</Pressable>
						</View>
					))}

					<View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
						<View style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
							<TextInput
								value={draft}
								onChangeText={setDraft}
								placeholder="Add a requirement…"
								onSubmitEditing={addItem}
								testID="requirement-draft-input"
							/>
						</View>
						<Button mode="outlined" onPress={addItem} disabled={!draft.trim()} testID="btn-add-requirement">
							Add
						</Button>
					</View>
				</ScrollView>

				<View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
					<Button mode="outlined" onPress={onDismiss}>Cancel</Button>
					<Button
						mode="contained"
						loading={busy}
						disabled={busy || !name.trim()}
						testID="btn-create-request-type"
						onPress={() => onCreate({
							name: name.trim(),
							slug: toSlug(name),
							requiredAttachments: items.map((item) => ({
								key: toKey(item.label),
								label: item.label,
								required: item.required,
							})),
							reviewDeadlineDays: Number(days) > 0 ? Number(days) : 30,
						})}
					>
						Create
					</Button>
				</View>
			</View>
		</Modal>
	);
};

export default RequestTypeCreateModal;
